import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { SyncLoader } from 'react-spinners';
import ImgNotFound from 'ud-ui/components/images/not-found.png';
import { useIsAdmin } from 'shared/hooks/use-is-admin';
import { Pages } from 'modules/navigation/domain/enums/pages';
import UDHeaderTitle from 'ud-ui/components/header-title';
import UDText from 'ud-ui/components/text';
import { useMainLayout } from 'ud-ui/layout/main/context';
import { OrganizerToCardDataMapper } from '../../../domain/data-mappers/orginizerToCard.data-mapper';
import { Organizer } from '../../../domain/interfaces/organizer';
import { filteringByPhone } from '../../../domain/utils/filtering-by-phone';
import { filteringBySearch } from '../../../domain/utils/filtering-by-search';
import { filteringBySportType } from '../../../domain/utils/filtering-by-sport-type';
import { sortingByDate } from '../../../domain/utils/sorting-by-date';
import { setOrganizers } from '../../../store';
import { selectOrganizersOptions } from '../../../store/selectors';
import Header from '../../components/header';
import OrganizerRow from '../../components/organizer-row';
import useOrganizers from '../../hooks/use-organizers';
import { MainBlock, RowsContainer } from './styles';

function OrganizersList() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { organizers, isLoaded } = useOrganizers();
  const [processedListOrganizers, setProcessedListOrganizers] = useState({
    bySearch: [] as Organizer[],
    byPhone: [] as Organizer[],
    bySortByDate: [] as Organizer[],
    bySportType: [] as Organizer[],
  });
  const { filter, filterByNumber, sortByDate, filterBySportType } = useSelector(selectOrganizersOptions);
  const mapper = useMemo(() => {
    return new OrganizerToCardDataMapper();
  }, []);
  const { setHeaderContent } = useMainLayout();

  useEffect(() => {
    if (!organizers) {
      return;
    }
    dispatch(setOrganizers(organizers));
    setProcessedListOrganizers({
      bySearch: organizers,
      byPhone: organizers,
      bySortByDate: organizers,
      bySportType: organizers,
    });
  }, [dispatch, organizers]);

  useEffect(() => {
    const filteringResult = filteringBySearch(organizers, filter?.toLowerCase().trim()) ?? [];
    setProcessedListOrganizers((prevState) => ({ ...prevState, bySearch: filteringResult }));
  }, [filter, organizers]);

  // FilterByPhone
  useEffect(() => {
    const filteringResult = filteringByPhone(processedListOrganizers.bySearch, filterByNumber) ?? [];
    setProcessedListOrganizers((prevState) => ({ ...prevState, byPhone: filteringResult }));
  }, [filterByNumber, organizers, processedListOrganizers.bySearch]);

  useEffect(() => {
    const filteringResult = filteringBySportType(processedListOrganizers.byPhone, filterBySportType) ?? [];
    setProcessedListOrganizers((prevState) => ({ ...prevState, bySportType: filteringResult }));
  }, [filterBySportType, processedListOrganizers.byPhone]);

  useEffect(() => {
    const filteringResult = sortingByDate(processedListOrganizers.bySportType, sortByDate) ?? [];
    setProcessedListOrganizers((prevState) => ({ ...prevState, bySortByDate: filteringResult }));
  }, [processedListOrganizers.bySportType, sortByDate]);

  const organizersList = useMemo(() => {
    if (!processedListOrganizers.bySortByDate) {
      return;
    }
    return processedListOrganizers.bySortByDate.map((organizer) => {
      const onCLick = () => {
        navigate(Pages.ORGANIZERS.builders.view(organizer.id));
      };

      return <OrganizerRow key={organizer.id} onClick={onCLick} {...mapper.decode(organizer)} />;
    });
  }, [mapper, navigate, processedListOrganizers.bySortByDate]);

  useEffect(() => {
    setHeaderContent(<UDHeaderTitle>Организаторы</UDHeaderTitle>);
    return () => setHeaderContent(null);
  }, [setHeaderContent]);

  const isAdmin = useIsAdmin();

  if (!isAdmin) {
    return null;
  }

  return (
    <>
      <MainBlock>
        <Header />
        <RowsContainer>
          {!isLoaded && (
            <div className={'flex-center container'} style={{ height: '100%' }}>
              <SyncLoader />
            </div>
          )}
          {organizersList?.length
            ? organizersList
            : isLoaded && (
                <div className={'flex-center container'} style={{ height: '100%', flexDirection: 'column' }}>
                  <img style={{ width: 130, height: 130 }} alt={'not found'} src={ImgNotFound} />
                  <UDText type={'callout'} className={'color-SurfaceRomanSilver30'}>
                    По такому запросу ничего не найдено
                  </UDText>
                </div>
              )}
        </RowsContainer>
      </MainBlock>
    </>
  );
}

export default OrganizersList;
